import styles from './styles.module.css'

import React, { useMemo, useState } from 'react'

import { getUserInboxes } from '@disco/state-management/selectors/groups'
import { canFormatBeOptimised } from '@disco/utils/file-utils'

import type { Group } from 'types/group'
import type { Track } from 'types/track'

import {
  Avatar,
  Badge,
  Box,
  Flex,
  RadioGroup,
  ScrollArea,
  SearchTextField,
  Skeleton,
  Table,
  Text,
} from 'components/ds'

import { useSelector } from 'hooks/use-redux'

import { useInboxTracks } from './use-inbox-tracks'

export type InboxSelection = {
  track: Track
  ext: string
  inboxName: string
}

type Props = {
  needsLossless: boolean
  selectedTrackId: number | null
  onSelect: (selection: InboxSelection) => void
}

const SKELETON_ROWS = 4

function findMatchingExt(track: Track, needsLossless: boolean) {
  return Object.keys(track.all_files ?? {}).find(
    ext => canFormatBeOptimised(ext) === needsLossless,
  )
}

function findInboxName(track: Track, inboxes: Group[]) {
  const inbox = inboxes.find(group => track.groups?.includes(group.url))
  return inbox ? inbox.internal_name || inbox.name : ''
}

/**
 * Searchable list of the user's inbox tracks to pick a replacement file from.
 * Tracks without a file of the needed kind are listed but disabled.
 * @param needsLossless - Whether the replacement must be a lossless file
 * @param selectedTrackId - The currently selected inbox track
 * @param onSelect - Called with the chosen track, its matching file extension and inbox name
 */
export default function InboxTrackPicker({
  needsLossless,
  selectedTrackId,
  onSelect,
}: Props) {
  const inboxes = useSelector(getUserInboxes)
  const inboxIds = useMemo(() => inboxes.map(inbox => inbox.id), [inboxes])
  const [query, setQuery] = useState('')
  const { tracks, loading } = useInboxTracks(inboxIds, query)
  const missingKindLabel = needsLossless ? 'lossless' : 'MP3 or M4A'

  const handleValueChange = (value: string) => {
    const track = tracks.find(item => String(item.id) === value)
    const ext = track && findMatchingExt(track, needsLossless)
    if (track && ext) {
      onSelect({ track, ext, inboxName: findInboxName(track, inboxes) })
    }
  }

  const renderList = () => {
    if (loading) {
      return (
        <Flex direction="column" gap="2">
          {Array.from({ length: SKELETON_ROWS }, (_, index) => (
            <Skeleton key={index} height="40px" />
          ))}
        </Flex>
      )
    }

    if (tracks.length === 0) {
      return (
        <Text size="2" color="gray">
          {query
            ? `No inbox tracks match "${query}".`
            : 'Nothing in your inboxes yet.'}
        </Text>
      )
    }

    return (
      <RadioGroup.Root
        value={selectedTrackId ? String(selectedTrackId) : ''}
        onValueChange={handleValueChange}
      >
        <Table.Root size="1">
          <Table.Body>
            {tracks.map(track => {
              const isUsable = Boolean(findMatchingExt(track, needsLossless))
              const inboxName = findInboxName(track, inboxes)
              return (
                <Table.Row
                  key={track.id}
                  align="center"
                  aria-disabled={!isUsable}
                  className={styles.inboxRow}
                  onClick={() => handleValueChange(String(track.id))}
                >
                  <Table.Cell width="1px">
                    <RadioGroup.Item
                      value={String(track.id)}
                      disabled={!isUsable}
                      aria-label={track.name}
                    />
                  </Table.Cell>
                  <Table.Cell width="1px">
                    <Avatar
                      size="2"
                      radius="small"
                      src={
                        track.artwork_sizes?.['160x160'] ??
                        track.artwork ??
                        undefined
                      }
                      fallback={track.name.charAt(0)}
                    />
                  </Table.Cell>
                  <Table.Cell maxWidth="0">
                    <Text as="div" size="2" weight="medium" truncate>
                      {track.name}
                    </Text>
                    <Text as="div" size="1" color="gray" truncate>
                      {[track.artist, inboxName].filter(Boolean).join(' · ')}
                    </Text>
                    {isUsable ? null : (
                      <Text as="div" size="1" color="gray">
                        No {missingKindLabel} file
                      </Text>
                    )}
                  </Table.Cell>
                  <Table.Cell justify="end" width="1px">
                    <Flex gap="1" justify="end">
                      {Object.keys(track.all_files ?? {}).map(ext => (
                        <Badge key={ext} size="1" color="gray">
                          {ext.toUpperCase()}
                        </Badge>
                      ))}
                    </Flex>
                  </Table.Cell>
                </Table.Row>
              )
            })}
          </Table.Body>
        </Table.Root>
      </RadioGroup.Root>
    )
  }

  return (
    <Flex direction="column" gap="3" className={styles.fill}>
      <SearchTextField
        placeholder="Search your inboxes"
        value={query}
        onChange={event => setQuery(event.target.value)}
      />
      <ScrollArea scrollbars="vertical" className={styles.inboxList}>
        <Box pr="3">{renderList()}</Box>
      </ScrollArea>
    </Flex>
  )
}
